import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { apiErrorMessage } from '@/lib/apiError';
import { esAdmin } from '@/lib/roles';
import { lugar } from '@/lib/lugar';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { 
  Package, DollarSign, Key, Clock, ArrowRight, 
  TrendingUp, Shield, Loader2
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DashboardOverview = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  // Un error se muestra como error, no como ceros y "No shipments yet" (revision del 26-oct, E.6).
  const [loadError, setLoadError] = useState('');
  const { getAuthHeaders, user } = useAuth();
  // Lo que solo ve el admin:
  // - SellVerify, que todavia no esta verificado (lanzamiento del 26-oct, igual
  //   que en el menu);
  // - las llaves de transportista de API Secrets, que solo se cuentan aca:
  //   ninguna cotizacion ni etiqueta las usa (§9.159).
  const verAdmin = esAdmin(user);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await axios.get(`${API}/dashboard/stats`, {
          headers: getAuthHeaders()
        });
        setStats(response.data);
        setLoadError('');
      } catch (error) {
        console.error('Failed to fetch stats:', error);
        setLoadError(apiErrorMessage(error.response?.data, 'Could not load your dashboard. Please try again.'));
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, [getAuthHeaders]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="py-16 text-center space-y-4" data-testid="overview-error">
        <p className="text-sm text-red-600">{loadError}</p>
        <Button variant="outline" onClick={() => window.location.reload()}>Try again</Button>
      </div>
    );
  }

  const statCards = [
    {
      title: 'Total Shipments',
      value: stats?.total_shipments || 0,
      icon: Package,
      color: 'text-blue-500',
      bgColor: 'bg-blue-50 dark:bg-blue-900/20'
    },
    {
      title: 'Total Spend',
      value: `$${(stats?.total_spend || 0).toFixed(2)}`,
      icon: DollarSign,
      color: 'text-emerald-500',
      bgColor: 'bg-emerald-50 dark:bg-emerald-900/20'
    },
    {
      title: 'Active Integrations',
      value: stats?.active_integrations || 0,
      icon: Key,
      color: 'text-purple-500',
      bgColor: 'bg-purple-50 dark:bg-purple-900/20',
      soloAdmin: true
    },
    {
      title: 'Pending Shipments',
      value: stats?.pending_shipments || 0,
      icon: Clock,
      color: 'text-amber-500',
      bgColor: 'bg-amber-50 dark:bg-amber-900/20'
    }
  ].filter((card) => verAdmin || !card.soloAdmin);

  return (
    <div className="space-y-8 animate-fade-in" data-testid="dashboard-overview">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-semibold tracking-tight">Dashboard</h1>
          <p className="text-slate-500 mt-1">Welcome back! Here's your shipping overview.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Link to="/dashboard/rate-shop">
            <Button className="btn-press" data-testid="create-shipment-btn">
              Create Shipment
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </div>

      {/* Stats Grid */}
      {/* El comerciante ve 3 contadores y el admin 4: la grilla sigue a la cantidad. */}
      <div className={`grid gap-4 md:gap-6 ${statCards.length === 3 ? 'sm:grid-cols-3' : 'sm:grid-cols-2 lg:grid-cols-4'}`}>
        {statCards.map((stat, index) => (
          <Card key={index} className="stat-card" data-testid={`stat-card-${index}`}>
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm text-slate-500 mb-1">{stat.title}</p>
                  <p className="text-2xl md:text-3xl font-semibold tabular-nums">{stat.value}</p>
                </div>
                <div className={`w-12 h-12 rounded-xl ${stat.bgColor} flex items-center justify-center`}>
                  <stat.icon className={`w-6 h-6 ${stat.color}`} />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Quick Actions: el admin tiene tres tarjetas; el comerciante solo
          "Compare Rates", que ocupa la fila entera. */}
      <div className={`grid gap-4 md:gap-6 ${verAdmin ? 'md:grid-cols-2 lg:grid-cols-3' : ''}`}>
        {verAdmin && (
        <Card className="bg-gradient-to-br from-primary to-primary/80 text-primary-foreground">
          <CardContent className="p-6">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                <Shield className="w-6 h-6" />
              </div>
              <div>
                <h3 className="font-medium text-lg">SellVerify</h3>
                <p className="text-sm opacity-80">AI-powered fraud protection</p>
              </div>
            </div>
            <Link to="/dashboard/verification">
              <Button variant="secondary" size="sm" className="btn-press" data-testid="verify-now-btn">
                Verify Now
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </CardContent>
        </Card>
        )}

        {verAdmin && (
        <Card className="border-dashed border-2 hover:border-solid hover:border-secondary transition-all">
          <CardContent className="p-6">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-12 h-12 bg-slate-100 dark:bg-slate-800 rounded-xl flex items-center justify-center">
                <Key className="w-6 h-6 text-slate-500" />
              </div>
              <div>
                <h3 className="font-medium text-lg">Connect Carriers</h3>
                <p className="text-sm text-slate-500">Add your API keys</p>
              </div>
            </div>
            <Link to="/dashboard/secrets">
              <Button variant="outline" size="sm" className="btn-press" data-testid="add-secrets-btn">
                Add Secrets
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </CardContent>
        </Card>
        )}

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-12 h-12 bg-emerald-50 dark:bg-emerald-900/20 rounded-xl flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-emerald-500" />
              </div>
              <div>
                <h3 className="font-medium text-lg">Compare Rates</h3>
                <p className="text-sm text-slate-500">Get best shipping prices</p>
              </div>
            </div>
            <Link to="/dashboard/rate-shop">
              <Button variant="outline" size="sm" className="btn-press" data-testid="compare-rates-btn">
                Get Rates
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>

      {/* Recent Activity */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Recent Shipments</CardTitle>
            <Link to="/dashboard/shipments">
              <Button variant="ghost" size="sm" data-testid="view-all-shipments-btn">
                View All
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </CardHeader>
        <CardContent>
          {stats?.recent_shipments?.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Shipment ID</th>
                    <th>Destination</th>
                    <th>Status</th>
                    <th>Cost</th>
                    <th>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.recent_shipments.map((shipment) => (
                    <tr key={shipment.id}>
                      <td className="font-mono text-sm">{shipment.id.slice(0, 8)}...</td>
                      <td>{lugar(shipment.ship_to)}</td>
                      <td>
                        <span className={`status-badge status-${shipment.status || 'unknown'}`}>
                          {shipment.status || '—'}
                        </span>
                      </td>
                      <td className="tabular-nums">{shipment.total_cost == null ? '—' : `$${Number(shipment.total_cost).toFixed(2)}`}</td>
                      <td className="text-slate-500">
                        {shipment.created_at ? new Date(shipment.created_at).toLocaleDateString() : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-12">
              <Package className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500 mb-4">No shipments yet</p>
              <Link to="/dashboard/rate-shop">
                <Button data-testid="first-shipment-btn">Create Your First Shipment</Button>
              </Link>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default DashboardOverview;
